import type {
  RiskAssessmentSection,
  RiskAssessmentTemplate,
  RiskAssessmentTemplateStatus
} from '@gcv/shared';
import { injectable } from 'inversify';

import { banksApi } from './api-util/api';

export interface GetRiskAssessmentTemplatesParams {
  status?: RiskAssessmentTemplateStatus;
  /** Which versions to return, e.g. 'draft', 'latest', or 'draft,latest'. Omit for latest published only. */
  version?: string;
  last_updated_only?: boolean;
  include_archived?: boolean;
}

export interface CreateRiskAssessmentTemplatePayload {
  name: string;
  description?: string;
  sections?: RiskAssessmentSection[];
  /** Resolved from the bank's published band sets when omitted; required when it can't be inferred. */
  risk_band_id?: string;
}

/** Omitted fields are left as they are, but the API rejects a PUT without a name, so always send it. */
export interface UpdateRiskAssessmentTemplatePayload {
  name: string;
  description?: string;
  sections?: RiskAssessmentSection[];
  risk_band_id?: string;
}

export interface DeleteRiskAssessmentTemplateResult {
  outcome: 'deleted' | 'archived';
  versions: string[];
}

@injectable()
export class RiskAssessmentTemplatesApi {
  async getRiskAssessmentTemplatesForBank(
    bankId: string,
    params?: GetRiskAssessmentTemplatesParams
  ): Promise<RiskAssessmentTemplate[]> {
    return await banksApi().get(
      `/banks/${bankId}/risk-assessment-templates`,
      params as Record<string, unknown> | undefined
    );
  }

  async getRiskAssessmentTemplate(bankId: string, recordId: string): Promise<RiskAssessmentTemplate> {
    return await banksApi().get(`/banks/${bankId}/risk-assessment-templates/${recordId}`);
  }

  async createRiskAssessmentTemplate(
    bankId: string,
    data: CreateRiskAssessmentTemplatePayload
  ): Promise<RiskAssessmentTemplate> {
    return await banksApi().post(`/banks/${bankId}/risk-assessment-templates`, data);
  }

  async updateRiskAssessmentTemplate(
    bankId: string,
    id: string,
    data: UpdateRiskAssessmentTemplatePayload
  ): Promise<RiskAssessmentTemplate> {
    return await banksApi().put(`/banks/${bankId}/risk-assessment-templates/${id}`, data);
  }

  async deleteRiskAssessmentTemplate(
    bankId: string,
    id: string
  ): Promise<DeleteRiskAssessmentTemplateResult> {
    return await banksApi().delete(`/banks/${bankId}/risk-assessment-templates/${id}`);
  }

  async publishRiskAssessmentTemplate(bankId: string, id: string): Promise<RiskAssessmentTemplate> {
    return await banksApi().post(`/banks/${bankId}/risk-assessment-templates/${id}/publish`, {});
  }
}
